import { ButtonV3, showToast } from '@provi/provi-components'
import { captureEvent } from '../../../utils/posthog'
import { POSTHOG_EVENTS } from '../../../constants/posthog'
import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { FaqDrawer } from '../../../components/faq-drawer'
import { MaxWidth } from '../../../components/max-width'
import { Template } from '../../../components/template'
import {
  getCrid,
  formatCurrency as formatCurrencyValue,
  formatDate as formatDateValue,
  getSummaryInfoFromStorage,
  mapStorageToFormattedData
} from '../helpers'
import { useNegotiationSimulation, useNegotiationSignature, useNegotiationAwaitingSignature } from '../hooks'

import { CashPaymentSection } from './components/CashPaymentSection'
import { CustomEntrySection } from './components/CustomEntrySection'
import { EntrySection } from './components/EntrySection'
import { ErrorAlert } from './components/ErrorAlert'
import { InfoBanner } from './components/InfoBanner'
import { InstallmentsSection } from './components/InstallmentsSection'
import { MobileActionBar } from './components/MobileActionBar'
import { SummaryCard } from './components/SummaryCard'
import { Tabs } from './components/Tabs'
import {
  PageContainer,
  PageGrid,
  ContentCard,
  PageTitle,
  MobilePageTitle,
  ButtonWrapper,
  Divider,
  AgreementDescription,
  AgreementSummary,
  SummarySection,
  SectionTitle,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  SummaryDivider,
  InstallmentRow,
  InstallmentLabel,
  InstallmentValue,
  InstallmentDate,
  InstallmentRightSide,
  ViewAllLink,
  TotalPaidHeader,
  TotalPaidLabel,
  TotalPaidValue,
  ImportantBanner,
  WarningIconWrapper,
  ImportantContent,
  ImportantTitle,
  ImportantText,
  SignButton,
  SignButtonIcon,
  SignButtonText,
  DisclaimerText,
  DisclaimerLink,
  AwaitingHeader,
  AwaitingHeaderIcon,
  AwaitingHeaderText,
  AwaitingDescription,
  AwaitingSectionTitle,
  SignaturesWrapper,
  SignatureColumn,
  SignatureHeader,
  SignatureTitle,
  SignatureStatus,
  SignatureInfo,
  SignatureName,
  SignatureDetail,
  InfoNote,
  InfoIconWrapper,
  InfoNoteText,
  SupportText,
  SupportLink,
  ProcessingWrapper,
  ProcessingContainer,
  ProcessingIconWrapper,
  ProcessingText
} from './styles'

const buildInstallmentsFromSelection = () => {
  try {
    const selectedEntryStr = localStorage.getItem('negotiation_selected_entry')
    const selectedInstallmentStr = localStorage.getItem('negotiation_selected_installment')

    if (!selectedEntryStr || !selectedInstallmentStr) {
      return null
    }

    const selectedEntry = JSON.parse(selectedEntryStr)
    const selectedInstallment = JSON.parse(selectedInstallmentStr)

    if (!selectedEntry || !selectedInstallment) {
      return null
    }

    const parcelas = []

    if (selectedEntry?.value) {
      parcelas.push({
        label: 'Entrada',
        valor: formatCurrencyValue(selectedEntry.value),
        data: formatDateValue(selectedEntry.dueDate)
      })
    }

    const installmentsCount = selectedInstallment?.installments || 0
    const installmentValue = selectedInstallment?.installmentValue
    const firstDueDate = selectedInstallment?.firstDueDate

    if (installmentsCount > 0 && installmentValue && firstDueDate) {
      const firstDate = new Date(firstDueDate)

      for (let i = 0; i < installmentsCount; i++) {
        const dueDate = new Date(firstDate)
        dueDate.setMonth(dueDate.getMonth() + i)

        parcelas.push({
          label: `${i + 1}ª parcela`,
          valor: formatCurrencyValue(installmentValue),
          data: formatDateValue(dueDate.toISOString())
        })
      }
    }

    return parcelas.length > 0 ? parcelas : null
  } catch (error) {
    console.error('Erro ao construir parcelas da seleção:', error)
    return null
  }
}

const TCD_ISA_URL = 'https://provi-addition-contract-production.s3.us-east-1.amazonaws.com/TCD_ISA_PADRAO.pdf'
const TCD_CCB_URL =
  'https://provi-addition-contract-production.s3.us-east-1.amazonaws.com/TCD_CCB_PADRAO_COM_DEVEDOR_SOLIDARIO.pdf'

const getContractUrl = () => {
  try {
    const cridData = JSON.parse(localStorage.getItem('CRID'))
    return cridData?.isIsa ? TCD_ISA_URL : TCD_CCB_URL
  } catch {
    return TCD_CCB_URL
  }
}
const SignatureContentCard = ({ onSign, data, isLoading }) => {
  const [showAllInstallments, setShowAllInstallments] = useState(false)

  if (isLoading || !data) {
    return (
      <ContentCard>
        <ProcessingContainer
          style={{
            padding: '60px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px'
          }}
        >
          <ProcessingIconWrapper>
            <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g>
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 60 60"
                  to="360 60 60"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
                <rect x="30" y="15" width="60" height="11.25" rx="5.625" fill="#2563EB" />
                <rect x="30" y="93.75" width="60" height="11.25" rx="5.625" fill="#2563EB" />
                <path
                  d="M39.375 26.25H80.625L67.5 45L67.5 52.5L80.625 71.25H39.375L52.5 52.5L52.5 45L39.375 26.25Z"
                  stroke="#0F172A"
                  strokeWidth="3.75"
                  strokeLinejoin="round"
                />
                <path d="M50.625 54.375H69.375" stroke="#0F172A" strokeWidth="3.75" strokeCap="round" />
              </g>
            </svg>
          </ProcessingIconWrapper>
          <ProcessingText>Aguarde um momento...</ProcessingText>
        </ProcessingContainer>
      </ContentCard>
    )
  }

  const visibleParcelas = showAllInstallments ? data.parcelas : data.parcelas?.slice(0, 4)

  const hasMoreThanFour = data.parcelas?.length > 4

  return (
    <ContentCard>
      <PageTitle>Assinatura do termo de renegociação</PageTitle>
      <AgreementDescription>Confirme os detalhes e assine para oficializar seu novo acordo.</AgreementDescription>
      <AgreementSummary>
        <SummarySection>
          <SectionTitle>Resumo geral</SectionTitle>
          <SummaryRow>
            <SummaryLabel>Saldo devedor negociado</SummaryLabel>
            <SummaryValue>{data.saldoDevedorNegociado}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Valor a refinanciar</SummaryLabel>
            <SummaryValue>{data.valorARefinanciar}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Validade da proposta</SummaryLabel>
            <SummaryValue>{data.validadeDaProposta}</SummaryValue>
          </SummaryRow>
        </SummarySection>

        <SummaryDivider />

        <SummarySection>
          <SectionTitle>Resumo do acordo</SectionTitle>
          <SummaryRow>
            <SummaryLabel>Entrada</SummaryLabel>
            <SummaryValue>{data.valorEntrada}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Novas parcelas</SummaryLabel>
            <SummaryValue>{data.novasParcelasResumo}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Vencimento da 1ª parcela</SummaryLabel>
            <SummaryValue>{data.vencimentoPrimeiraParcela}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Taxa de juros do contrato</SummaryLabel>
            <SummaryValue>{data.taxaJurosContrato}</SummaryValue>
          </SummaryRow>
        </SummarySection>

        <SummaryDivider />

        <SummarySection>
          <SectionTitle>Detalhamento das parcelas</SectionTitle>
          {visibleParcelas?.map((parcela, index) => (
            <InstallmentRow key={index}>
              <InstallmentLabel>{parcela.label}</InstallmentLabel>
              <InstallmentRightSide>
                <InstallmentValue>{parcela.valor}</InstallmentValue>
                <InstallmentDate>{parcela.data}</InstallmentDate>
              </InstallmentRightSide>
            </InstallmentRow>
          ))}
          {hasMoreThanFour && (
            <ViewAllLink
              href="#"
              onClick={e => {
                e.preventDefault()
                setShowAllInstallments(!showAllInstallments)
              }}
            >
              {showAllInstallments ? 'Ver menos' : 'Ver todas as parcelas'}
            </ViewAllLink>
          )}
        </SummarySection>

        <TotalPaidHeader>
          <TotalPaidLabel>Total pago a prazo</TotalPaidLabel>
          <TotalPaidValue>{data.totalPagoAPrazo}</TotalPaidValue>
        </TotalPaidHeader>

        <ImportantBanner>
          <WarningIconWrapper>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#F97316" />
              <path d="M12 7V11" stroke="white" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="15" r="1" fill="white" />
            </svg>
          </WarningIconWrapper>
          <ImportantContent>
            <ImportantTitle>Importante</ImportantTitle>
            <ImportantText>
              As condições desta negociação são exclusivas para este momento. Os encargos do seu contrato são apurados
              diariamente e uma nova negociação futura terá valores diferentes.
            </ImportantText>
          </ImportantContent>
        </ImportantBanner>

        <SignButton onClick={onSign}>
          <SignButtonIcon>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5.2025 13.309H15V14.9757H0V11.4398L8.25 3.18982L11.785 6.72649L5.20167 13.309H5.2025ZM9.4275 2.01232L11.1958 0.243989C11.3521 0.0877629 11.564 0 11.785 0C12.006 0 12.2179 0.0877629 12.3742 0.243989L14.7317 2.60149C14.8879 2.75776 14.9757 2.96969 14.9757 3.19066C14.9757 3.41163 14.8879 3.62355 14.7317 3.77982L12.9633 5.54732L9.42833 2.01232H9.4275Z"
                fill="white"
              />
            </svg>
          </SignButtonIcon>
          <SignButtonText>Assinar termo</SignButtonText>
        </SignButton>

        <DisclaimerText>
          Clicando em "Assinar termo", você concorda que leu e está de acordo com o{' '}
          <DisclaimerLink href={getContractUrl()} target="_blank" rel="noopener noreferrer">
            Termo de Confissão de Dívida
          </DisclaimerLink>
          .
        </DisclaimerText>
      </AgreementSummary>
    </ContentCard>
  )
}

export const NegotiationSignature = () => {
  const history = useHistory()
  const crid = getCrid()
  const { formattedData: apiFormattedData, isLoading: apiIsLoading, handleSign, isSigning } = useNegotiationSignature(crid)

  const summaryInfo = getSummaryInfoFromStorage()
  const storageFormattedData = summaryInfo ? mapStorageToFormattedData(summaryInfo) : null

  const preservedCalculation = storageFormattedData?.calculation || summaryInfo?.calculation

  const selectedInstallments = buildInstallmentsFromSelection()

  let selectedEntry = null
  let selectedInstallment = null
  try {
    const selectedEntryStr = localStorage.getItem('negotiation_selected_entry')
    const selectedInstallmentStr = localStorage.getItem('negotiation_selected_installment')
    selectedEntry = selectedEntryStr ? JSON.parse(selectedEntryStr) : null
    selectedInstallment = selectedInstallmentStr ? JSON.parse(selectedInstallmentStr) : null
  } catch (error) {
    console.error('Erro ao recuperar dados selecionados:', error)
  }

  const selectedData =
    selectedEntry && selectedInstallment
      ? {
          valorEntrada: formatCurrencyValue(selectedEntry.value),
          dataVencimentoEntrada: formatDateValue(selectedEntry.dueDate),
          quantidadeParcelas: selectedInstallment.installments,
          valorParcela: formatCurrencyValue(selectedInstallment.installmentValue),
          dataVencimentoPrimeiraParcela: formatDateValue(selectedInstallment.firstDueDate),
          vencimentoPrimeiraParcela: formatDateValue(selectedInstallment.firstDueDate),
          novasParcelasResumo: `${selectedInstallment.installments}x de ${formatCurrencyValue(
            selectedInstallment.installmentValue
          )}`,
          totalPagoAPrazo: formatCurrencyValue(
            (selectedEntry.value || 0) + selectedInstallment.installmentValue * selectedInstallment.installments
          ),
          parcelas: selectedInstallments
        }
      : null

  const formattedData = selectedData
    ? {
        ...selectedData,
        ...(storageFormattedData && {
          saldoDevedorAtual: storageFormattedData.saldoDevedorAtual,
          saldoDevedorAnterior: storageFormattedData.saldoDevedorAnterior,
          percentualDesconto: storageFormattedData.percentualDesconto,
          calculation: preservedCalculation || storageFormattedData.calculation,
          saldoDevedorNegociado: storageFormattedData.saldoDevedorAtual || storageFormattedData.saldoDevedorNegociado,
          valorARefinanciar: (() => {
            const refinancingValue = localStorage.getItem('negotiation_refinancing_value')
            return refinancingValue || storageFormattedData.valorARefinanciar || null
          })(),
          validadeDaProposta: selectedData?.dataVencimentoEntrada || storageFormattedData.validadeDaProposta,
          taxaJurosContrato: (() => {
            const interestRate = localStorage.getItem('negotiation_interest_rate')
            if (interestRate) {
              const rate = parseFloat(interestRate)
              return `${rate.toFixed(2).replace('.', ',')}% a.m.`
            }
            return '2,99% a.m.'
          })()
        }),
        ...(apiFormattedData && {
          termUrl: apiFormattedData.termUrl,
          saldoDevedorNegociado:
            storageFormattedData?.saldoDevedorAtual ||
            apiFormattedData.saldoDevedorNegociado ||
            storageFormattedData?.saldoDevedorNegociado,
          valorARefinanciar: (() => {
            const refinancingValue = localStorage.getItem('negotiation_refinancing_value')
            return refinancingValue || apiFormattedData.valorARefinanciar || storageFormattedData?.valorARefinanciar || null
          })(),
          validadeDaProposta:
            selectedData?.dataVencimentoEntrada ||
            apiFormattedData.validadeDaProposta ||
            storageFormattedData?.validadeDaProposta,
          taxaJurosContrato: (() => {
            const interestRate = localStorage.getItem('negotiation_interest_rate')
            if (interestRate) {
              const rate = parseFloat(interestRate)
              return `${rate.toFixed(2).replace('.', ',')}% a.m.`
            }
            return apiFormattedData?.taxaJurosContrato || '2,99% a.m.'
          })()
        })
      }
    : apiFormattedData && apiFormattedData.parcelas?.length > 0
    ? {
        ...storageFormattedData,
        parcelas: apiFormattedData.parcelas,
        calculation: preservedCalculation || storageFormattedData?.calculation || apiFormattedData.calculation,
        valorEntrada: apiFormattedData.valorEntrada,
        novasParcelasResumo: apiFormattedData.novasParcelasResumo,
        dataVencimentoEntrada: apiFormattedData.dataVencimentoEntrada,
        dataVencimentoPrimeiraParcela: apiFormattedData.dataVencimentoPrimeiraParcela,
        vencimentoPrimeiraParcela: apiFormattedData.vencimentoPrimeiraParcela,
        totalPagoAPrazo: apiFormattedData.totalPagoAPrazo,
        quantidadeParcelas: apiFormattedData.quantidadeParcelas,
        valorParcela: apiFormattedData.valorParcela,
        saldoDevedorAtual: apiFormattedData.saldoDevedorAtual || storageFormattedData?.saldoDevedorAtual,
        saldoDevedorAnterior: apiFormattedData.saldoDevedorAnterior || storageFormattedData?.saldoDevedorAnterior,
        percentualDesconto: apiFormattedData.percentualDesconto || storageFormattedData?.percentualDesconto,
        saldoDevedorNegociado:
          storageFormattedData?.saldoDevedorAtual ||
          apiFormattedData.saldoDevedorNegociado ||
          storageFormattedData?.saldoDevedorNegociado,
        valorARefinanciar: apiFormattedData.valorARefinanciar || storageFormattedData?.valorARefinanciar,
        validadeDaProposta:
          apiFormattedData.dataVencimentoEntrada ||
          apiFormattedData.validadeDaProposta ||
          storageFormattedData?.validadeDaProposta,
        taxaJurosContrato: '2,99% a.m.',
        termUrl: apiFormattedData.termUrl || storageFormattedData?.termUrl
      }
    : storageFormattedData || apiFormattedData

  const isLoading = selectedData
    ? false
    : storageFormattedData && (!apiFormattedData || !apiFormattedData.parcelas?.length)
    ? false
    : apiIsLoading

  const onSign = () => {
    handleSign(redirectUrl => {
      history.push(redirectUrl || '/dashboard/negociacao/processing')
    })
  }

  return (
    <Template>
      <FaqDrawer />
      <PageContainer>
        <MaxWidth>
          <PageGrid>
            <SignatureContentCard onSign={onSign} data={formattedData} isLoading={isLoading || isSigning} />
            <SummaryCard
              screen="renegociacao_assinatura"
              isLoading={isLoading}
              discount={{ percentage: formattedData?.percentualDesconto }}
              outstanding={{
                currentValue: formattedData?.saldoDevedorAtual,
                previousValue: formattedData?.saldoDevedorAnterior
              }}
              calculation={formattedData?.calculation}
              paymentPlan={[
                { id: 'entry', label: 'Entrada', type: 'text', value: formattedData?.valorEntrada },
                {
                  id: 'installments',
                  label: 'Parcelas',
                  type: 'text',
                  value: formattedData?.novasParcelasResumo
                },
                {
                  id: 'total',
                  label: 'Total pago a prazo',
                  type: 'text',
                  value: formattedData?.totalPagoAPrazo,
                  highlight: true
                }
              ]}
              schedule={[
                { id: 'entry-due', label: 'Venc. da entrada', date: formattedData?.dataVencimentoEntrada },
                { id: 'first-due', label: 'Venc. 1ª parcela', date: formattedData?.dataVencimentoPrimeiraParcela }
              ]}
            />
          </PageGrid>
        </MaxWidth>
      </PageContainer>
    </Template>
  )
}

export const NegotiationProcessing = () => {
  const history = useHistory()

  useEffect(() => {
    const timer = setTimeout(() => {
      history.push('/dashboard/negociacao/aguardando-assinatura')
    }, 3000)

    return () => clearTimeout(timer)
  }, [history])

  return (
    <Template>
      <FaqDrawer />
      <PageContainer>
        <MaxWidth>
          <ProcessingWrapper>
            <ProcessingContainer>
              <ProcessingIconWrapper>
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g>
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 60 60"
                      to="360 60 60"
                      dur="1.5s"
                      repeatCount="indefinite"
                    />
                    <rect x="30" y="15" width="60" height="11.25" rx="5.625" fill="#2563EB" />
                    <rect x="30" y="93.75" width="60" height="11.25" rx="5.625" fill="#2563EB" />
                    <path
                      d="M39.375 26.25H80.625L67.5 45L67.5 52.5L80.625 71.25H39.375L52.5 52.5L52.5 45L39.375 26.25Z"
                      stroke="#0F172A"
                      strokeWidth="3.75"
                      strokeLinejoin="round"
                    />
                    <path d="M50.625 54.375H69.375" stroke="#0F172A" strokeWidth="3.75" strokeLinecap="round" />
                  </g>
                </svg>
              </ProcessingIconWrapper>
              <ProcessingText>Aguarde um momento...</ProcessingText>
            </ProcessingContainer>
          </ProcessingWrapper>
        </MaxWidth>
      </PageContainer>
    </Template>
  )
}

export const NegotiationAwaitingSignature = () => {
  const crid = getCrid()
  const { formattedData: apiFormattedData, isLoading } = useNegotiationAwaitingSignature(crid)

  // Recuperar dados do localStorage como fallback para o summary card
  const summaryInfo = getSummaryInfoFromStorage()
  const storageFormattedData = summaryInfo ? mapStorageToFormattedData(summaryInfo) : null

  // Recuperar dados selecionados da simulação
  let selectedEntry = null
  let selectedInstallment = null
  try {
    const selectedEntryStr = localStorage.getItem('negotiation_selected_entry')
    const selectedInstallmentStr = localStorage.getItem('negotiation_selected_installment')
    selectedEntry = selectedEntryStr ? JSON.parse(selectedEntryStr) : null
    selectedInstallment = selectedInstallmentStr ? JSON.parse(selectedInstallmentStr) : null
  } catch (error) {
    console.error('Erro ao recuperar dados selecionados:', error)
  }

  const selectedData =
    selectedEntry && selectedInstallment
      ? {
          valorEntrada: formatCurrencyValue(selectedEntry.value),
          dataVencimentoEntrada: formatDateValue(selectedEntry.dueDate),
          quantidadeParcelas: selectedInstallment.installments,
          valorParcela: formatCurrencyValue(selectedInstallment.installmentValue),
          dataVencimentoPrimeiraParcela: formatDateValue(selectedInstallment.firstDueDate),
          novasParcelasResumo: `${selectedInstallment.installments}x de ${formatCurrencyValue(
            selectedInstallment.installmentValue
          )}`,
          totalPagoAPrazo: formatCurrencyValue(
            (selectedEntry.value || 0) + selectedInstallment.installmentValue * selectedInstallment.installments
          )
        }
      : null

  const formattedData = apiFormattedData
    ? {
        ...apiFormattedData,
        saldoDevedorAtual: storageFormattedData?.saldoDevedorAtual || apiFormattedData.saldoDevedorAtual,
        saldoDevedorAnterior: storageFormattedData?.saldoDevedorAnterior || apiFormattedData.saldoDevedorAnterior,
        calculation: storageFormattedData?.calculation || apiFormattedData.calculation,
        valorEntrada: selectedData?.valorEntrada || storageFormattedData?.valorEntrada || apiFormattedData.valorEntrada,
        quantidadeParcelas:
          selectedData?.quantidadeParcelas || storageFormattedData?.quantidadeParcelas || apiFormattedData.quantidadeParcelas,
        valorParcela: selectedData?.valorParcela || storageFormattedData?.valorParcela || apiFormattedData.valorParcela,
        novasParcelasResumo:
          selectedData?.novasParcelasResumo ||
          storageFormattedData?.novasParcelasResumo ||
          apiFormattedData.novasParcelasResumo,
        totalPagoAPrazo:
          selectedData?.totalPagoAPrazo || storageFormattedData?.totalPagoAPrazo || apiFormattedData.totalPagoAPrazo,
        dataVencimentoEntrada:
          selectedData?.dataVencimentoEntrada ||
          storageFormattedData?.dataVencimentoEntrada ||
          apiFormattedData.dataVencimentoEntrada,
        dataVencimentoPrimeiraParcela:
          selectedData?.dataVencimentoPrimeiraParcela ||
          storageFormattedData?.dataVencimentoPrimeiraParcela ||
          apiFormattedData.dataVencimentoPrimeiraParcela
      }
    : null

  if (isLoading || !formattedData) {
    return (
      <Template>
        <FaqDrawer />
        <PageContainer>
          <MaxWidth>
            <ContentCard>
              <PageTitle>Carregando...</PageTitle>
            </ContentCard>
          </MaxWidth>
        </PageContainer>
      </Template>
    )
  }

  return (
    <Template>
      <FaqDrawer />
      <PageContainer>
        <MaxWidth>
          <PageGrid>
            <ContentCard>
              <AwaitingHeader>
                <AwaitingHeaderIcon>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10 20C4.477 20 0 15.523 0 10C0 4.477 4.477 0 10 0C15.523 0 20 4.477 20 10C20 15.523 15.523 20 10 20ZM10 18C12.1217 18 14.1566 17.1571 15.6569 15.6569C17.1571 14.1566 18 12.1217 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 12.1217 2.84285 14.1566 4.34315 15.6569C5.84344 17.1571 7.87827 18 10 18ZM11 10H15V12H9V5H11V10Z"
                      fill="white"
                      fillOpacity="0.92"
                    />
                  </svg>
                </AwaitingHeaderIcon>
                <AwaitingHeaderText>Aguardando assinaturas</AwaitingHeaderText>
              </AwaitingHeader>

              <AwaitingDescription>
                Para o acordo de renegociação ser validado, o aluno e o avalista precisam assinar o documento digitalmente.
              </AwaitingDescription>

              <AwaitingSectionTitle>Assinaturas</AwaitingSectionTitle>
              <SignaturesWrapper>
                <SignatureColumn>
                  <SignatureHeader>
                    <SignatureTitle>Assinatura do aluno</SignatureTitle>
                    <SignatureStatus variant={formattedData.statusAssinaturaAlunoVariant}>
                      {formattedData.statusAssinaturaAlunoTexto}
                    </SignatureStatus>
                  </SignatureHeader>
                  <SignatureInfo>
                    <SignatureName>{formattedData.nomeAluno}</SignatureName>
                    <SignatureDetail>CPF {formattedData.cpfAluno}</SignatureDetail>
                    <SignatureDetail>{formattedData.emailAluno}</SignatureDetail>
                  </SignatureInfo>
                </SignatureColumn>
                {formattedData.hasGuarantor && (
                  <SignatureColumn>
                    <SignatureHeader>
                      <SignatureTitle>Assinatura do avalista</SignatureTitle>
                      <SignatureStatus variant={formattedData.statusAssinaturaAvalistaVariant}>
                        {formattedData.statusAssinaturaAvalistaTexto}
                      </SignatureStatus>
                    </SignatureHeader>
                    <SignatureInfo>
                      <SignatureName>{formattedData.nomeAvalista}</SignatureName>
                      <SignatureDetail>CPF {formattedData.cpfAvalista}</SignatureDetail>
                      <SignatureDetail>{formattedData.emailAvalista}</SignatureDetail>
                    </SignatureInfo>
                  </SignatureColumn>
                )}
              </SignaturesWrapper>

              <InfoNote>
                <InfoIconWrapper>
                  <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.4688 20C14.0234 20 17.3047 18.125 19.1016 15C20.8984 11.9141 20.8984 8.125 19.1016 5C17.3047 1.91406 14.0234 0 10.4688 0C6.875 0 3.59375 1.91406 1.79688 5C0 8.125 0 11.9141 1.79688 15C3.59375 18.125 6.875 20 10.4688 20ZM8.90625 13.125H9.84375V10.625H8.90625H7.96875V8.75H8.90625H10.7812H11.7188V9.6875V13.125H12.0312H12.9688V15H12.0312H8.90625H7.96875V13.125H8.90625ZM11.7188 7.5H9.21875V5H11.7188V7.5Z"
                      fill="#0088CC"
                    />
                  </svg>
                </InfoIconWrapper>
                <InfoNoteText>
                  O avalista receberá o link de assinatura exclusivamente no e-mail cadastrado. Após a conclusão da assinatura
                  digital, a fatura para pagamento da entrada será enviada automaticamente para o seu e-mail.
                </InfoNoteText>
              </InfoNote>

              <ImportantBanner>
                <WarningIconWrapper>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#F97316" />
                    <path d="M12 7V11" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="15" r="1" fill="white" />
                  </svg>
                </WarningIconWrapper>
                <ImportantContent>
                  <ImportantTitle>Importante</ImportantTitle>
                  <ImportantText>
                    As condições desta negociação são exclusivas para este momento. Os encargos do seu contrato são apurados
                    diariamente e uma nova negociação futura terá valores diferentes.
                  </ImportantText>
                </ImportantContent>
              </ImportantBanner>

              <Divider />
              <SupportText>
                Se precisar de ajuda, entre em <SupportLink href="#">contato com o suporte.</SupportLink>
              </SupportText>
            </ContentCard>
            <SummaryCard
              variant="awaiting"
              showDiscount={false}
              isLoading={isLoading}
              outstanding={{
                currentValue: formattedData.saldoDevedorAtual,
                previousValue: formattedData.saldoDevedorAnterior
              }}
              calculation={formattedData.calculation}
              paymentPlan={[
                { id: 'entry', label: 'Entrada', type: 'text', value: formattedData.valorEntrada },
                {
                  id: 'installments',
                  label: 'Parcelas',
                  type: 'text',
                  value:
                    formattedData.novasParcelasResumo ||
                    (formattedData.quantidadeParcelas && formattedData.valorParcela
                      ? `${formattedData.quantidadeParcelas}x de ${formattedData.valorParcela}`
                      : '-')
                },
                {
                  id: 'total',
                  label: 'Total pago a prazo',
                  type: 'text',
                  value: formattedData.totalPagoAPrazo,
                  highlight: true
                }
              ]}
              schedule={[
                { id: 'entry-due', label: 'Venc. da entrada', date: formattedData.dataVencimentoEntrada },
                { id: 'first-due', label: 'Venc. 1ª parcela', date: formattedData.dataVencimentoPrimeiraParcela }
              ]}
              signatureStatus={{
                student: {
                  status: formattedData.statusAssinaturaAlunoVariant,
                  statusText: formattedData.statusAssinaturaAlunoTexto,
                  name: formattedData.nomeAluno,
                  cpf: formattedData.cpfAluno,
                  email: formattedData.emailAluno
                },
                guarantor: formattedData.hasGuarantor
                  ? {
                      status: formattedData.statusAssinaturaAvalistaVariant,
                      statusText: formattedData.statusAssinaturaAvalistaTexto,
                      name: formattedData.nomeAvalista,
                      cpf: formattedData.cpfAvalista,
                      email: formattedData.emailAvalista
                    }
                  : null
              }}
            />
          </PageGrid>
        </MaxWidth>
      </PageContainer>
    </Template>
  )
}

export const NegotiationSimulation = () => {
  const history = useHistory()
  const [activeTab, setActiveTab] = useState('avista')
  const [showCustomEntry, setShowCustomEntry] = useState(false)
  const [showError, setShowError] = useState(false)
  const crid = getCrid()

  React.useEffect(() => {
    if (!crid) {
      showToast('Ocorreu um erro inesperado. Redirecionando para a página inicial.')
      history.push('/dashboard')
    }
  }, [crid, history])

  const {
    formattedData,
    isLoading,
    error,
    selectedEntry,
    selectedInstallment,
    setSelectedInstallment,
    data,
    refetch,
    refetchWithUpFront,
    customUpFront,
    isAccepting,
    handleAcceptProposal
  } = useNegotiationSimulation(crid)

  React.useEffect(() => {
    if (error && !isLoading) {
      setShowError(true)
    }
  }, [error, isLoading])

  const handleRetry = () => {
    setShowError(false)
    refetch()
  }

  return (
    <Template>
      <FaqDrawer />
      <PageContainer>
        <MaxWidth>
          <PageGrid>
            <MobilePageTitle>Renegocie seu contrato</MobilePageTitle>
            <ContentCard>
              <PageTitle>Renegocie seu contrato</PageTitle>
              <ErrorAlert
                show={showError}
                title="Ocorreu um erro ao carregar a proposta."
                description={
                  <>
                    Por favor,{' '}
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        handleRetry()
                      }}
                    >
                      tente novamente
                    </a>
                    . Caso o problema persista, entre em contato com o suporte.
                  </>
                }
                onClose={() => setShowError(false)}
              />
              <Tabs activeTab={activeTab} onChange={setActiveTab} />
              {activeTab === 'parcelado' ? (
                <>
                  {!showCustomEntry ? (
                    <>
                      <EntrySection
                        isLoading={isLoading}
                        entryOptions={data?.entryOptions}
                        formattedValue={formattedData?.valorEntradaSelecionada}
                        formattedDueDate={formattedData?.dataVencimentoEntrada}
                        badge={formattedData?.badgeEntrada}
                        onChangeEntry={() => setShowCustomEntry(true)}
                      />
                      <Divider />
                      <InstallmentsSection
                        isLoading={isLoading}
                        installmentOptions={data?.installmentOptions}
                        selectedInstallment={selectedInstallment}
                        onSelectInstallment={setSelectedInstallment}
                        formattedOptions={formattedData?.opcoesParcelas}
                        formattedSelected={formattedData?.opcaoParcelamentoSelecionada}
                      />
                    </>
                  ) : (
                    <CustomEntrySection
                      onSave={value => {
                        refetchWithUpFront(value)
                        setShowCustomEntry(false)
                      }}
                      onCancel={() => setShowCustomEntry(false)}
                      selectedEntry={selectedEntry}
                      minEntryValue={data?.summary?.minEntryValue}
                      formattedDueDate={formattedData?.dataVencimentoEntrada}
                      validationMessages={data?.summary?.entryValidation}
                    />
                  )}
                  <InfoBanner isLoading={isLoading} show={true} />
                  <ButtonWrapper>
                    <ButtonV3
                      text="Aceitar proposta"
                      width="100%"
                      mobileWidth="100%"
                      height="56px"
                      onClick={() => {
                        captureEvent(POSTHOG_EVENTS.PROPOSTA_ACEITA, {
                          numero_pedido_compra: String(crid),
                          valor_total_divida: data?.summary?.outstandingDebt,
                          valor_total_negociado: selectedInstallment?.totalValue,
                          valor_entrada_informado: selectedEntry?.value,
                          entrada_minima_permitida: data?.summary?.minEntryValue,
                          parcelamento_informado: selectedInstallment?.installments,
                          valor_parcela: selectedInstallment?.installmentValue,
                          desconto_aplicado: data?.summary?.discountPercentage, //check
                          desconto_percentual_aplicado: data?.summary?.discountPercentage,
                          tipo_negociacao: 'renegociação',
                          resultado_simulacao: 'sucesso',
                          ajuste_manual_entrada: customUpFront ? true:false,
                        })

                        localStorage.setItem('negotiationId', formattedData?.installmentNegotiationId)
                        localStorage.setItem('negotiation_selected_entry', JSON.stringify(selectedEntry))
                        localStorage.setItem('negotiation_selected_installment', JSON.stringify(selectedInstallment))

                        if (formattedData?.refinancingValue) {
                          localStorage.setItem('negotiation_refinancing_value', formattedData.refinancingValue)
                        }
                        if (formattedData?.interestRate !== undefined && formattedData?.interestRate !== null) {
                          localStorage.setItem('negotiation_interest_rate', formattedData.interestRate.toString())
                        }
                        history.push('/dashboard/negociacao/assinatura')
                      }}
                      disabled={isLoading || isAccepting}
                    />
                  </ButtonWrapper>
                </>
              ) : (
                <CashPaymentSection
                  isLoading={isLoading || isAccepting}
                  totalValue={formattedData?.valorPagamentoAVista}
                  discountValue={formattedData?.descontoPagamentoAVista}
                  validityDate={formattedData?.validadeProposta}
                  onAccept={() => {
                    captureEvent(POSTHOG_EVENTS.PROPOSTA_ACEITA, {
                      numero_pedido_compra: String(crid),
                      valor_total_divida: data?.summary?.outstandingDebt,
                      valor_total_negociado: data?.summary?.cashPaymentValue,
                      valor_entrada_informado: null,
                      entrada_minima_permitida: data?.summary?.minEntryValue,
                      parcelamento_informado: 0,
                      valor_parcela: null,
                      desconto_aplicado: data?.summary?.cashPaymentDiscount,
                      desconto_percentual_aplicado: data?.summary?.cashPaymentDiscountPercentage,
                      tipo_negociacao: 'quitação',
                      resultado_simulacao: 'sucesso',
                      ajuste_manual_entrada: false,
                    })

                    handleAcceptProposal({
                      isQuitation: true,
                      onSuccess: response => {
                        localStorage.removeItem('negotiation_installments_count')
                        localStorage.removeItem('negotiation_installment_value')

                        localStorage.setItem('negotiationId', formattedData?.cashNegotiationId)
                        if (response?.data?.payment) {
                          const payment = response.data.payment
                          if (payment.amount) {
                            const amountInReais = parseFloat(payment.amount)
                            const formattedAmount = formatCurrencyValue(amountInReais)
                            localStorage.setItem('negotiation_entry_value', formattedAmount)
                          }
                          if (payment.dueDate) {
                            const formattedDate = formatDateValue(payment.dueDate)
                            localStorage.setItem('negotiation_entry_due_date', formattedDate)
                          }
                          if (payment.pixQrCode) localStorage.setItem('negotiation_pix_qr_code', payment.pixQrCode)
                          if (payment.pixQrCodeText) localStorage.setItem('negotiation_pix_code', payment.pixQrCodeText)
                          if (payment.barCode) localStorage.setItem('negotiation_bar_code', payment.barCode)
                          if (payment.boleto) localStorage.setItem('negotiation_boleto_url', payment.boleto)
                          if (payment.InvoiceId) localStorage.setItem('negotiation_invoice_id', String(payment.InvoiceId))
                        } else {
                          if (formattedData?.valorPagamentoAVista) {
                            localStorage.setItem('negotiation_entry_value', formattedData.valorPagamentoAVista)
                          }
                          if (formattedData?.dataVencimentoPagamentoAVista) {
                            localStorage.setItem('negotiation_entry_due_date', formattedData.dataVencimentoPagamentoAVista)
                          }
                        }
                        history.push('/dashboard/negociacao/pagamento')
                      }
                    })
                  }}
                />
              )}
            </ContentCard>
            <SummaryCard
              screen="renegociacao_simulacao"
              isLoading={isLoading}
              cashPayment={activeTab === 'avista'}
              discount={{
                percentage: activeTab === 'avista' ? formattedData?.percentualDescontoAVista : formattedData?.percentualDesconto
              }}
              outstanding={{
                currentValue: activeTab === 'avista' ? formattedData?.valorPagamentoAVista : formattedData?.saldoDevedorAtual,
                previousValue: formattedData?.saldoDevedorAnterior
              }}
              calculation={formattedData?.calculation}
              paymentPlan={
                activeTab === 'avista'
                  ? [
                      {
                        id: 'total',
                        label: 'Total à vista',
                        type: 'text',
                        value: formattedData?.valorPagamentoAVista,
                        highlight: true
                      }
                    ]
                  : [
                      { id: 'entry', label: 'Entrada', type: 'text', value: formattedData?.valorEntradaSelecionada },
                      {
                        id: 'installments',
                        label: 'Parcelas',
                        type: 'text',
                        value: formattedData?.opcaoParcelamentoSelecionada
                      },
                      {
                        id: 'total',
                        label: 'Total pago a prazo',
                        type: 'text',
                        value: formattedData?.totalPagoAPrazo,
                        highlight: true
                      }
                    ]
              }
              schedule={
                activeTab === 'avista'
                  ? [{ id: 'payment-due', label: 'Venc. do pagamento', date: formattedData?.dataVencimentoPagamentoAVista }]
                  : [
                      { id: 'entry-due', label: 'Venc. da entrada', date: formattedData?.dataVencimentoEntrada },
                      { id: 'first-due', label: 'Venc. 1ª parcela', date: formattedData?.dataVencimentoPrimeiraParcela }
                    ]
              }
            />
          </PageGrid>
        </MaxWidth>
        <MobileActionBar
          totalLabel={activeTab === 'avista' ? 'Total à vista' : 'Total pago a prazo'}
          totalValue={activeTab === 'avista' ? formattedData?.valorPagamentoAVista : formattedData?.totalPagoAPrazo}
          validityDate={formattedData?.validadeProposta}
          entryValue={activeTab === 'parcelado' ? formattedData?.valorEntradaSelecionada : null}
          installmentsText={activeTab === 'parcelado' ? formattedData?.opcaoParcelamentoSelecionada : null}
          buttonText="Aceitar proposta"
          onAccept={() => {
            const isQuitation = activeTab === 'avista'

            captureEvent(POSTHOG_EVENTS.PROPOSTA_ACEITA, {
              numero_pedido_compra: String(crid),
              valor_total_divida: data?.summary?.outstandingDebt,
              valor_total_negociado: isQuitation ? data?.summary?.cashPaymentValue : selectedInstallment?.totalValue,
              valor_entrada_informado: isQuitation ? data?.summary?.cashPaymentValue : selectedEntry?.value,
              entrada_minima_permitida: data?.summary?.minEntryValue,
              parcelamento_informado: isQuitation ? 0 : selectedInstallment?.installments,
              valor_parcela: isQuitation ? 0 : selectedInstallment?.installmentValue,
              desconto_aplicado: isQuitation ? data?.summary?.cashPaymentDiscount : null,
              desconto_percentual_aplicado: isQuitation
                ? data?.summary?.cashPaymentDiscountPercentage
                : data?.summary?.discountPercentage,
              tipo_negociacao: isQuitation ? 'quitação' : 'renegociação',
              resultado_simulacao: 'sucesso',
              ajuste_manual_entrada: customUpFront ? true:false ,
            })

            if (isQuitation) {
              // À vista: chama accept e vai para pagamento
              handleAcceptProposal({
                isQuitation: true,
                onSuccess: response => {
                  localStorage.removeItem('negotiation_installments_count')
                  localStorage.removeItem('negotiation_installment_value')

                  localStorage.setItem('negotiationId', formattedData?.cashNegotiationId)
                  if (response?.data?.payment) {
                    const payment = response.data.payment
                    if (payment.amount) {
                      const amountInReais = parseFloat(payment.amount)
                      const formattedAmount = formatCurrencyValue(amountInReais)
                      localStorage.setItem('negotiation_entry_value', formattedAmount)
                    }
                    if (payment.dueDate) {
                      const formattedDate = formatDateValue(payment.dueDate)
                      localStorage.setItem('negotiation_entry_due_date', formattedDate)
                    }
                    if (payment.pixQrCode) localStorage.setItem('negotiation_pix_qr_code', payment.pixQrCode)
                    if (payment.pixQrCodeText) localStorage.setItem('negotiation_pix_code', payment.pixQrCodeText)
                    if (payment.barCode) localStorage.setItem('negotiation_bar_code', payment.barCode)
                    if (payment.boleto) localStorage.setItem('negotiation_boleto_url', payment.boleto)
                    if (payment.InvoiceId) localStorage.setItem('negotiation_invoice_id', String(payment.InvoiceId))
                  } else {
                    if (formattedData?.valorPagamentoAVista) {
                      localStorage.setItem('negotiation_entry_value', formattedData.valorPagamentoAVista)
                    }
                    if (formattedData?.dataVencimentoPagamentoAVista) {
                      localStorage.setItem('negotiation_entry_due_date', formattedData.dataVencimentoPagamentoAVista)
                    }
                  }
                  history.push('/dashboard/negociacao/pagamento')
                }
              })
            } else {
              // Parcelado: apenas salva dados e redireciona para assinatura
              // O accept será chamado na tela de assinatura ao assinar o termo
              // IMPORTANTE: NÃO limpar dados de parcelas aqui - eles serão salvos após assinatura
              localStorage.setItem('negotiationId', formattedData?.installmentNegotiationId)
              localStorage.setItem('negotiation_selected_entry', JSON.stringify(selectedEntry))
              localStorage.setItem('negotiation_selected_installment', JSON.stringify(selectedInstallment))
              history.push('/dashboard/negociacao/assinatura')
            }
          }}
          disabled={isLoading || isAccepting}
          isLoading={isLoading || isAccepting}
        />
      </PageContainer>
    </Template>
  )
}
